import { useProductSearchQuery } from '@api/product.api';
import { BuyersCertificate, BuyersCertificateDetailsResponse } from '@api/types';
import { useSerialNumber } from '@hooks';
import { getDealer } from '@store/dealer/dealer.slice';
import debounce from 'lodash/debounce';

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

enum SerialNoRequestStatus {
  Pending = 'pending',
  Valid = 'valid',
  Invalid = 'invalid',
}

type BuyerCertificateCreateContextProps = {
  children: React.ReactNode;
  currentData?: BuyersCertificateDetailsResponse;
};

type BuyerCertificateCreateContextType = {
  form: UseFormReturn<BuyersCertificate>;
  formValues: BuyersCertificate;
  isEditEnabled: boolean;
  toggleEdit: () => void;
  serialNumberOnChange: (value: string) => void;
  isSerialNoValid: boolean | null;
  getSerialNoStatus: () => SerialNoRequestStatus | null;
  SerialNoRequestStatus: typeof SerialNoRequestStatus;
};

export const BuyerCertificateCreateContext =
  createContext<BuyerCertificateCreateContextType | null>(null);

export const BuyersCertificateCreateProvider = ({
  children,
  currentData,
}: BuyerCertificateCreateContextProps) => {
  useProductSearchQuery({});

  const dealer = useSelector(getDealer);
  const form = useForm<BuyersCertificate>({
    defaultValues: {
      dealerId: dealer.id ?? '',
      dealerName: dealer.name ?? '',
      ...currentData?.data,
    },
  });
  const formValues = form.getValues();
  const controller = useRef<AbortController | null>(null);
  const [isEditEnabled, setEditEnabled] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const [isSerialNoValid, setSerialNoValid] = useState<boolean | null>(null);
  const { sanitizeSerialNo } = useSerialNumber();

  const getSerialNoStatus = useCallback((): SerialNoRequestStatus | null => {
    if (status === 'pending') return SerialNoRequestStatus.Pending;
    if (isSerialNoValid) return SerialNoRequestStatus.Valid;
    if (isSerialNoValid === false) return SerialNoRequestStatus.Invalid;
    return null;
  }, [isSerialNoValid, status]);

  const checkSerialNumber = useCallback(
    async (value?: string) => {
      const sanitizedValue = sanitizeSerialNo(value);
      setSerialNoValid(null);
      setStatus('pending');
      try {
        if (sanitizedValue) {
          form.clearErrors('serialNumber');
          const res = await fetch(`/api/serial-number/verify?serialNumber=${sanitizedValue}`, {
            signal: controller.current?.signal,
          });
          if (!res.ok) {
            setSerialNoValid(false);
            setStatus('invalid');

            form.setValue('isSerialNumberValid', false);
            return;
          }
          setSerialNoValid(true);
          setStatus('fulfilled');

          form.setValue('isSerialNumberValid', true);
        } else {
          setStatus('invalid');
          setSerialNoValid(false);
          form.setValue('isSerialNumberValid', false);
        }
      } catch (error) {
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            setStatus('invalid');

            setSerialNoValid(null);
            form.setValue('isSerialNumberValid', false);
            return;
          }
        }
      }
    },
    [form, sanitizeSerialNo]
  );

  const serialNumberOnChange = useRef(
    debounce(async (value: string) => {
      if (controller.current) {
        controller.current.abort();
      }
      controller.current = new AbortController();
      form.clearErrors('serialNumber');
      if (!value) {
        setSerialNoValid(null);
        setStatus(null);
        return;
      }
      checkSerialNumber(value);
    }, 600)
  ).current;

  const toggleEdit = useCallback(() => {
    if (!isEditEnabled) {
      const value = form.getValues('serialNumber');
      checkSerialNumber(value);
    }
    if (isEditEnabled) {
      form.reset(
        {
          dealerId: dealer.id ?? '',
          dealerName: dealer.name ?? '',
          ...currentData?.data,
        },
        {
          keepErrors: false,
        }
      );
      form.clearErrors('serialNumber');
      setSerialNoValid(null);
    }
    setEditEnabled(!isEditEnabled);
  }, [form, isEditEnabled, currentData, dealer, checkSerialNumber]);

  const contextValue = useMemo(
    () => ({
      form,
      formValues,
      isEditEnabled,
      toggleEdit,
      serialNumberOnChange,
      isSerialNoValid,
      getSerialNoStatus,
      SerialNoRequestStatus,
    }),
    [
      form,
      formValues,
      isEditEnabled,
      toggleEdit,
      serialNumberOnChange,
      isSerialNoValid,
      getSerialNoStatus,
    ]
  );
  return (
    <BuyerCertificateCreateContext.Provider value={contextValue}>
      {children}
    </BuyerCertificateCreateContext.Provider>
  );
};

export const useBuyersCertificateCreateContext = () => {
  const context = useContext(BuyerCertificateCreateContext);
  if (!context) {
    throw new Error(
      'useBuyersCertificateCreateContext must be used within a BuyersCertificateCreateProvider'
    );
  }
  return context;
};
